import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { uploadOtaRelease } from "../api/otaAdminApi";

// valonix-os-2.2.21.raucb / valonix-hotfix-2.2.21-hf1.raucb (same rule as the server)
const NAME = /^valonix-(os|hotfix)-(\d+\.\d+\.\d+(?:-[0-9A-Za-z.-]+)?)\.raucb$/;
const MAX_NOTES = 1000; // travels in the query string
const mb = (n) => (n / 1048576).toFixed(1);

export function parseBundleName(name) {
  const m = NAME.exec(name || "");
  if (!m) return null;
  const hotfix = m[1] === "hotfix";
  return { hotfix, version: m[2], from: hotfix ? m[2].split("-")[0] : "*" };
}

const fieldStyle = { display: "grid", gap: 4 };

/** Pick a signed bundle, add notes, upload with a progress bar. The server hashes and publishes it. */
export default function OtaUploadForm({ accessToken, onDone, cardStyle = {} }) {
  const { t } = useTranslation();
  const [file, setFile] = useState(null);
  const [channel, setChannel] = useState("stable");
  const [fromVersion, setFromVersion] = useState("");
  const [notes, setNotes] = useState("");
  const [recoverySafe, setRecoverySafe] = useState(false);
  const [progress, setProgress] = useState(null);
  const abort = useRef(null);

  const parsed = parseBundleName(file?.name);
  const uploading = progress !== null;

  const submit = async (event) => {
    event.preventDefault();
    abort.current = new AbortController();
    setProgress({ loaded: 0, total: file.size });
    try {
      await uploadOtaRelease(
        accessToken,
        file,
        { release_notes: notes, channel, from_version: fromVersion.trim(), recovery_safe: recoverySafe },
        { onProgress: setProgress, signal: abort.current.signal },
      );
      toast.success(t("admin.ota.upload.done"));
      setFile(null);
      setNotes("");
      setFromVersion("");
      setRecoverySafe(false);
      await onDone?.();
    } catch (error) {
      if (error.code !== "ERR_CANCELED") toast.error(error.response?.data?.detail || t("admin.ota.upload.failed"));
    } finally {
      setProgress(null);
    }
  };

  const percent = progress && progress.total ? Math.min(100, Math.round((progress.loaded / progress.total) * 100)) : 0;

  return (
    <form onSubmit={submit} style={{ ...cardStyle, display: "grid", gap: 12 }}>
      <h2>{t("admin.ota.upload.title")}</h2>
      <label style={fieldStyle}>
        {t("admin.ota.upload.package")}
        <input type="file" accept=".raucb" disabled={uploading} onChange={(e) => {
          const next = e.target.files?.[0] || null;
          setFile(next);
          if (next && !parseBundleName(next.name)) toast.error(t("admin.ota.upload.badName"));
        }} />
      </label>
      {parsed && (
        <p style={{ margin: 0 }}>
          <strong>{parsed.hotfix ? t("admin.ota.upload.kindHotfix") : t("admin.ota.upload.kindOs")}</strong> · {t("admin.ota.upload.summary")} <strong>{parsed.version}</strong> · {mb(file.size)} MB ·
          {" "}{t("admin.ota.upload.hashNote")}
        </p>
      )}
      <label style={fieldStyle}>
        {t("admin.ota.upload.fromVersion", { auto: parsed ? (parsed.hotfix ? parsed.from : t("admin.ota.upload.allVersions")) : "—" })}
        <input value={fromVersion} disabled={uploading} placeholder={parsed?.from || ""} onChange={(e) => setFromVersion(e.target.value)} />
      </label>
      <label style={fieldStyle}>
        {t("admin.ota.upload.channel")}
        <select value={channel} disabled={uploading} onChange={(e) => setChannel(e.target.value)}>
          <option value="stable">Stable</option>
          <option value="canary">Canary</option>
          <option value="dev">Dev</option>
        </select>
      </label>
      <label style={fieldStyle}>
        {t("admin.ota.upload.notes", { length: notes.length, max: MAX_NOTES })}
        <textarea required rows={5} maxLength={MAX_NOTES} value={notes} disabled={uploading} onChange={(e) => setNotes(e.target.value)} />
      </label>
      <label>
        <input type="checkbox" checked={recoverySafe} disabled={uploading} onChange={(e) => setRecoverySafe(e.target.checked)} /> {t("admin.ota.upload.recoverySafe")}
      </label>
      {uploading && (
        <div style={{ display: "grid", gap: 4 }}>
          <progress value={percent} max={100} aria-label={t("admin.ota.upload.progressLabel")} style={{ width: "100%" }} />
          <span>
            {t("admin.ota.upload.progress", { percent, loaded: mb(progress.loaded), total: mb(progress.total) })}
            {percent === 100 ? t("admin.ota.upload.verifying") : ""}
          </span>
        </div>
      )}
      <div style={{ display: "flex", gap: 8 }}>
        <button type="submit" disabled={uploading || !parsed || !notes.trim()}>
          {t("admin.ota.upload.submit")}
        </button>
        {uploading && (
          <button type="button" onClick={() => abort.current?.abort()}>
            {t("admin.ota.upload.cancel")}
          </button>
        )}
      </div>
    </form>
  );
}
